import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import useComplianceAccess from "hooks/useComplianceAccess";
import complianceService from "services/compliance";
import {
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
} from "date-fns";
import { getCorporates } from "state/slices/contactPersonCorporates";

const ComplianceContext = createContext();

const STATUS_OPTIONS = [
  { value: "notDone", label: "Not done" },
  { value: "partiallyDone", label: "Partially Done" },
  { value: "completed", label: "Completed" },
];

const LIFECYCLE_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "notApplicable", label: "Not Applicable" },
  { value: "archived", label: "Archive" },
  { value: "delete", label: "Delete Task" },
];

const TIMELINE_TYPES = {
  DEFINITE: "definite",
  INDEFINITE: "indefinite",
};

const FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "bi-monthly", label: "Bi-Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "bi-annual", label: "Bi-Annual" },
  { value: "annual", label: "Annual" },
];

const roundToTwo = (value) => Math.round(value * 100) / 100;
const formatDate = (date) => format(date, "yyyy-MM-dd");
const today = () => new Date();

const addFrequency = (date, frequency) => {
  switch (frequency) {
    case "bi-monthly":
      return addMonths(date, 2);
    case "quarterly":
      return addQuarters(date, 1);
    case "bi-annual":
      return addMonths(date, 6);
    case "annual":
      return addYears(date, 1);
    case "monthly":
    default:
      return addMonths(date, 1);
  }
};

const computeTaskSummaryStatus = (task) => {
  if (!task || task.lifecycleStatus !== "active") {
    return {
      summaryStatus: "excluded",
      complianceTag: "Excluded",
      complianceColor: "bg-gray-200 text-gray-700",
    };
  }

  const now = today();
  const startDate = task.startDate ? parseISO(task.startDate) : null;
  const deadline = task.deadline ? parseISO(task.deadline) : null;

  if (task.status === "completed") {
    if (!task.completionDate) {
      return {
        summaryStatus: "due",
        complianceTag: "Due",
        complianceColor: "bg-amber-100 text-amber-700",
      };
    }
    const completionDate = parseISO(task.completionDate);
    if (
      deadline &&
      (isBefore(completionDate, deadline) || isEqual(completionDate, deadline))
    ) {
      return {
        summaryStatus: "compliant",
        complianceTag: "Compliant",
        complianceColor: "bg-emerald-100 text-emerald-700",
      };
    }
    return {
      summaryStatus: "notCompliant",
      complianceTag: "notCompliant",
      complianceColor: "bg-red-100 text-red-700",
    };
  }

  if (deadline && isBefore(deadline, now)) {
    return {
      summaryStatus: "notCompliant",
      complianceTag: "notCompliant",
      complianceColor: "bg-red-100 text-red-700",
    };
  }

  if (startDate && isAfter(startDate, now)) {
    return {
      summaryStatus: "notDue",
      complianceTag: "notDue",
      complianceColor: "bg-emerald-100 text-emerald-700",
    };
  }

  if (deadline && (isBefore(now, deadline) || isEqual(now, deadline))) {
    return {
      summaryStatus: "due",
      complianceTag: "Due",
      complianceColor: "bg-amber-100 text-amber-700",
    };
  }

  return {
    summaryStatus: "due",
    complianceTag: "Due",
    complianceColor: "bg-amber-100 text-amber-700",
  };
};

const buildDefaultResponsibilities = () => [
  {
    id: uuidv4(),
    title: "Default Registrar",
    department: "Corporate Services",
    individualId: null,
    initials: "DR",
    name: "Unassigned",
  },
];

const defaultFallbackUsers = [];
const defaultUsers = [];

const ENTITY_TYPES = {
  CORPORATE: "corporate",
  COMPANY: "company",
};

const defaultRegistrarIds = [];

const createTask = (overrides = {}) => {
  const task = {
    id: uuidv4(),
    title: "",
    description: "",
    checklistHeader: "",
    status: "notDone",
    lifecycleStatus: "active",
    statusDate: null,
    completionDate: null,
    startDate: null,
    deadline: null,
    dependency: null,
    notes: null,
    responsibilities: buildDefaultResponsibilities(),
    timelineType: TIMELINE_TYPES.DEFINITE,
    frequency: null,
    nextCheckDate: null,
    weight: 0,
    attachments: [],
    isMandatory: false,
    mandatoryBy: null,
    mandatorySourceId: null,
    ...overrides,
  };

  // Ensure metadata is present and synchronized for backend persistence
  task.metadata = {
    ...(task.metadata || {}),
    timelineType: task.timelineType,
    startDate: task.startDate,
    deadline: task.deadline,
    frequency: task.frequency,
    nextCheckDate: task.nextCheckDate,
    checklistHeader: task.checklistHeader,
  };

  const computed = computeTaskSummaryStatus(task);
  return { ...task, ...computed };
};

const cloneCategories = (categories) =>
  categories.map((category) => ({
    ...category,
    tasks: category.tasks.map((task) => ({ ...task })),
  }));

// ─── Default category builders (unchanged) ────────────────────────────────────

const buildFinancialReportingCategory = (year) => {
  const startOfYear = formatDate(new Date(year, 0, 1));
  const deadlineKra = formatDate(new Date(year, 5, 30));
  const deadlineRegistrar = formatDate(new Date(year, 8, 30));
  return {
    id: uuidv4(),
    name: "Financial Reporting",
    description: "Annual financial reporting obligations.",
    weight: 0,
    tasks: [
      createTask({
        title: "Set the Financial Year End",
        description:
          "Ensure the Financial Year end for the Company has been formally set.",
        startDate: startOfYear,
        deadline: startOfYear,
        responsibilities: buildDefaultResponsibilities(),
      }),
      createTask({
        title: "Engage Accountant",
        description:
          "Engage an Accountant to maintain the Books of Accounts of the Company.",
        notes:
          "If engagement is advisory, capture recommendation outcome for audit trail.",
        startDate: startOfYear,
        deadline: startOfYear,
      }),
      createTask({
        title: "Engage External Auditors",
        startDate: startOfYear,
        deadline: formatDate(new Date(year, 2, 31)),
      }),
      createTask({
        title: "Prepare Financial Statements",
        description:
          "Prepare financial statements in accordance with the Companies Act 2015 and IFRS.",
        startDate: startOfYear,
        deadline: deadlineKra,
      }),
      createTask({
        title: "Submit to KRA",
        description:
          "Submit audited financial statements and documents to the Kenya Revenue Authority.",
        startDate: startOfYear,
        deadline: deadlineKra,
      }),
      createTask({
        title: "Submit to Registrar of Companies",
        description:
          "Submit audited financial statements and documents to the Registrar of Companies.",
        startDate: startOfYear,
        deadline: deadlineRegistrar,
      }),
    ],
  };
};

const buildBoardMeetingsCategory = (year) => {
  const startDate = formatDate(new Date(year, 0, 15));
  const deadline = formatDate(new Date(year, 5, 30));
  return {
    id: uuidv4(),
    name: "Board Meetings",
    description: "Quarterly and AGM board meetings management.",
    weight: 0,
    tasks: [
      createTask({
        title: "Schedule Board Meetings",
        description:
          "Schedule and organize board meetings to approve quarterly and audited statements.",
        startDate,
        deadline,
        dependency: "Financial Reporting",
        responsibilities: buildDefaultResponsibilities(),
      }),
      createTask({
        title: "Set AGM Details",
        description:
          "Board to set the venue, date and time for the Annual General Meeting.",
        startDate,
        deadline: formatDate(new Date(year, 6, 31)),
        dependency: "Financial Reporting",
      }),
    ],
  };
};

const buildGeneralMeetingsCategory = (year) => ({
  id: uuidv4(),
  name: "General Meetings",
  description: "AGM planning, notices and minutes.",
  weight: 0,
  tasks: [
    createTask({
      title: "Schedule AGM",
      description: "Schedule and organize the AGM within the defined timeline.",
      startDate: formatDate(new Date(year, 5, 1)),
      deadline: formatDate(new Date(year, 6, 31)),
      dependency: "Approval of the Financial Statements",
      responsibilities: buildDefaultResponsibilities(),
    }),
    createTask({
      title: "Issue AGM Notices",
      description:
        "Issue notices and agenda to shareholders in accordance with the Companies Act 2015.",
      startDate: formatDate(new Date(year, 4, 15)),
      deadline: formatDate(new Date(year, 5, 15)),
    }),
    createTask({
      title: "Circulate AGM Minutes",
      description: "Prepare and circulate minutes of the AGM.",
      startDate: formatDate(new Date(year, 6, 1)),
      deadline: formatDate(new Date(year, 7, 15)),
    }),
  ],
});

const buildAnnualReturnsCategory = (year) => ({
  id: uuidv4(),
  name: "Filing of Annual Returns",
  description: "Annual returns compilation and filing.",
  weight: 0,
  tasks: [
    createTask({
      title: "Obtain Signed Financials",
      startDate: formatDate(new Date(year, 5, 1)),
      deadline: formatDate(new Date(year, 5, 15)),
      dependency: "Financial Reporting",
    }),
    createTask({
      title: "Update Director and Shareholder Details",
      startDate: formatDate(new Date(year, 4, 1)),
      deadline: formatDate(new Date(year, 5, 30)),
    }),
    createTask({
      title: "Compile Filing Documents",
      startDate: formatDate(new Date(year, 5, 1)),
      deadline: formatDate(new Date(year, 5, 20)),
    }),
    createTask({
      title: "File Annual Return",
      description:
        "Prepare the Annual Return within 28 days from the date of the return.",
      startDate: formatDate(new Date(year, 5, 15)),
      deadline: formatDate(new Date(year, 5, 30)),
      dependency: "Financial Reporting – For PLC, CLG and FC",
    }),
  ],
});

const buildStatutoryRegistersCategory = (year) => ({
  id: uuidv4(),
  name: "Maintenance of Statutory Registers",
  description: "Ensure statutory registers are accurate and up to date.",
  weight: 0,
  tasks: [
    createTask({
      title: "Update Registers",
      description:
        "Update and maintain the Register of Members, Directors, Secretaries and Charges.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "monthly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 0, 31)),
    }),
    createTask({
      title: "Record Changes",
      description:
        "Record changes in shareholders, Directors, Secretaries, Beneficial Owners and charges.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "monthly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 0, 31)),
    }),
    createTask({
      title: "Validate Registers",
      description: "Ensure accuracy and completeness of the Registers.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "quarterly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 2, 31)),
    }),
  ],
});

const buildDirectorsDutiesCategory = (year) => ({
  id: uuidv4(),
  name: "Director's Duties and Disclosures",
  description: "Manage director terms, disclosures and evaluations.",
  weight: 0,
  tasks: [
    createTask({
      title: "Set Director Term",
      description:
        "Set a Director's Term in Office to trigger Retirement or Rotation in Office.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "annual",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 0, 31)),
    }),
    createTask({
      title: "Educate Directors",
      description: "Educate directors about their duties and obligations.",
      startDate: formatDate(new Date(year, 0, 15)),
      deadline: formatDate(new Date(year, 1, 28)),
    }),
    createTask({
      title: "Conflict Disclosure Process",
      description:
        "Establish processes for conflict of interest disclosures and related party transactions.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "quarterly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 2, 31)),
    }),
    createTask({
      title: "Update Conflict Register",
      description: "Update the Register of Conflicts of Interest.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "bi-monthly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 1, 28)),
    }),
    createTask({
      title: "Board Evaluation",
      description: "Run the annual board evaluation.",
      startDate: formatDate(new Date(year, 9, 1)),
      deadline: formatDate(new Date(year, 10, 30)),
    }),
  ],
});

const buildShareCapitalCategory = (year) => ({
  id: uuidv4(),
  name: "Share Capital and Shareholder Rights",
  description:
    "Safeguard shareholder rights and accurate share capital records.",
  weight: 0,
  tasks: [
    createTask({
      title: "Maintain Share Capital Records",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "monthly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 0, 31)),
    }),
    createTask({
      title: "Safeguard Shareholder Rights",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "quarterly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 2, 31)),
    }),
    createTask({
      title: "Issue Share Certificates",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 0, 31)),
    }),
  ],
});

const buildCorporateGovernanceCategory = (year) => ({
  id: uuidv4(),
  name: "Corporate Governance",
  description: "Policies, committee charters and governance framework.",
  weight: 0,
  tasks: [
    createTask({
      title: "Establish Board Committees",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 2, 31)),
    }),
    createTask({
      title: "Ensure Governance Guideline Adherence",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "annual",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 0, 31)),
    }),
    createTask({
      title: "Update Board Policies",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "annual",
      startDate: formatDate(new Date(year, 8, 1)),
      deadline: formatDate(new Date(year, 8, 30)),
    }),
  ],
});

const buildMonitoringCategory = (year) => ({
  id: uuidv4(),
  name: "Monitoring and Reporting",
  description: "KPI tracking, structural changes and periodic audits.",
  weight: 0,
  tasks: [
    createTask({
      title: "Track Compliance KPIs",
      description:
        "Track KPIs such as days to next filing deadline and completion rates.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "monthly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 0, 31)),
      responsibilities: buildDefaultResponsibilities(),
    }),
    createTask({
      title: "Monitor Corporate Structure Changes",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "quarterly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 2, 31)),
    }),
    createTask({
      title: "Generate Compliance Reports",
      description:
        "Generate compliance status reports and membership lists for stakeholders.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "quarterly",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 2, 31)),
    }),
    createTask({
      title: "Set Internal Audits",
      description: "Set periodic internal audits to assess compliance status.",
      timelineType: TIMELINE_TYPES.INDEFINITE,
      frequency: "bi-annual",
      startDate: formatDate(new Date(year, 0, 1)),
      deadline: formatDate(new Date(year, 5, 30)),
    }),
  ],
});

const buildDefaultCategories = (year) => [
  buildFinancialReportingCategory(year),
  buildBoardMeetingsCategory(year),
  buildGeneralMeetingsCategory(year),
  buildAnnualReturnsCategory(year),
  buildStatutoryRegistersCategory(year),
  buildDirectorsDutiesCategory(year),
  buildShareCapitalCategory(year),
  buildCorporateGovernanceCategory(year),
  buildMonitoringCategory(year),
];

const buildCorporate = (
  id,
  name,
  organizationId,
  year,
  categories,
  metadata = {}
) => ({
  id,
  name,
  organizationId,
  year,
  categories: categories
    ? cloneCategories(categories)
    : buildDefaultCategories(year),
  template: null,
  templateHistory: [],
  entityType: metadata.entityType || ENTITY_TYPES.CORPORATE,
  parentCorporateId: metadata.parentCorporateId || null,
  annualReturnDate: metadata.annualReturnDate || null,
  registrationNumber: metadata.registrationNumber || null,
  orgAdminProfile: metadata.orgAdminProfile || null,
  // Org-slice metadata — always initialised so downstream code can safely
  // call .includes() / spread without undefined checks
  roles: Array.isArray(metadata.roles) ? metadata.roles : [],
  isRegistrar: metadata.isRegistrar ?? false,
  isOrgAdmin: metadata.isOrgAdmin ?? false,
  status: metadata.status || null,
  designation: metadata.designation || null,
  partnershipType: metadata.partnershipType || null,
});

// ─── Transform a raw org-slice entry into a compliance corporate ────────────────
/**
 * Converts a single item from the organizationsSlice (shape: { corporate, role, _id })
 * into the shape expected by the compliance context.
 *
 * Key mappings:
 *   corporate._id              → id
 *   corporate.names.companyName → name
 *   corporate.createdBy         → organizationId (best available org identifier)
 *   corporate.companyRegNumber  → registrationNumber
 *   corporate.isRegistrar       → preserved in metadata
 *   corporate.isOrgAdmin        → preserved in metadata
 *   corporate.status            → used for lifecycleStatus hint
 */
const mapOrgSliceEntryToComplianceCorporate = (entry, year) => {
  const { corporate } = entry;
  if (!corporate?._id) return null;

  const id = corporate._id;
  const name =
    corporate.names?.companyName ||
    corporate.identificationDetails?.companyContactName ||
    "Unnamed Corporate";
  // organizationId: use createdBy as the owning user/org identifier,
  // falling back to the entry's own _id
  const organizationId = corporate.createdBy || entry._id || id;

  const currentYear = year || new Date().getFullYear();

  return buildCorporate(id, name, organizationId, currentYear, null, {
    entityType: ENTITY_TYPES.CORPORATE,
    registrationNumber: corporate.companyRegNumber || null,
    annualReturnDate: corporate.annualReturnDate || null,
    orgAdminProfile: corporate.isOrgAdmin
      ? {
          name: corporate.identificationDetails?.companyContactName || name,
          email: corporate.identificationDetails?.emailAddress || null,
          phone: corporate.identificationDetails?.telNumber || null,
        }
      : null,
    // Preserve extra fields for downstream consumers
    isRegistrar: corporate.isRegistrar || false,
    isOrgAdmin: corporate.isOrgAdmin || false,
    status: corporate.status || "Active",
    designation: corporate.designation || null,
    partnershipType: corporate.partnershipType || null,
    roles: entry.role != null ? [entry.role] : [],
  });
};

/**
 * Deduplicate and transform the organizations array from Redux into
 * compliance corporates.  Multiple entries may share the same corporate._id
 * (different roles) — we keep only one record per _id, merging roles.
 */
const mapOrgsToCorporates = (organizations = [], year) => {
  const seen = new Map(); // corporateId → compliance corporate

  organizations.forEach((entry) => {
    const corporateId = entry?.corporate?._id;
    if (!corporateId) return;

    if (seen.has(corporateId)) {
      // Merge additional roles onto the existing record
      const existing = seen.get(corporateId);
      // Guard: roles may be undefined on backend-hydrated corporates that
      // didn't go through the mapper (e.g. from an earlier refetch merge)
      const currentRoles = Array.isArray(existing.roles) ? existing.roles : [];
      if (entry.role != null && !currentRoles.includes(entry.role)) {
        existing.roles = [...currentRoles, entry.role];
      }
    } else {
      const mapped = mapOrgSliceEntryToComplianceCorporate(entry, year);
      if (mapped) seen.set(corporateId, mapped);
    }
  });

  return Array.from(seen.values());
};

// ─── Unchanged helpers ────────────────────────────────────────────────────────

const SUPER_ADMIN_ID = "super-admin";
const SUPER_ADMIN_NAME = "SFL Super Admin";
const SUPER_ADMIN_ORIGIN = "superAdmin";
const SUPER_ADMIN_OWNER_TYPE = "superAdmin";
const SUPER_ADMIN_APPLIED_BY = "Super Admin";
const DEFAULT_TEMPLATE_ID = "tmpl-annual";
const DEFAULT_TEMPLATE_NAME = "Annual Compliance Tracker";

const buildDefaultData = () => ({
  corporates: [],
  companies: [],
  orgAdmins: [],
  templates: [],
  templateAssignments: { corporates: {}, companies: {} },
  organizationTemplates: [],
  templateHistory: [],
  superAdmin: { id: SUPER_ADMIN_ID, name: SUPER_ADMIN_NAME },
  orgAdminProfile: null,
  superAdminProfile: {
    id: "super-admin-gp001",
    code: "ORG001",
    corporateIds: [],
    companyIds: [],
  },
});

const recalculateTaskWeights = (category) => {
  const activeTasks = (category.tasks || []).filter(
    (task) => task.lifecycleStatus === "active"
  );
  const weightPerTask = activeTasks.length
    ? roundToTwo(Number(category.weight || 0) / activeTasks.length)
    : 0;
  return {
    ...category,
    tasks: (category.tasks || []).map((task) => {
      const updatedTask = {
        ...task,
        weight: task.lifecycleStatus === "active" ? weightPerTask : 0,
      };
      const computed = computeTaskSummaryStatus(updatedTask);
      return { ...updatedTask, ...computed };
    }),
  };
};

const recalculateCategoryWeights = (categories) => {
  if (!categories.length) return categories;
  return categories.map((category) => recalculateTaskWeights(category));
};

const useComplianceContext = () => useContext(ComplianceContext);

const flattenTasks = (entity) => {
  const entityType = entity.entityType || ENTITY_TYPES.CORPORATE;
  const entityLabel =
    entityType === ENTITY_TYPES.COMPANY ? "Company" : "Corporate";
  return entity.categories.flatMap((category) =>
    category.tasks.map((task) => ({
      ...task,
      corporateId: entity.id,
      corporateName: entity.name,
      entityId: entity.id,
      entityType,
      entityLabel,
      companyId: entityType === ENTITY_TYPES.COMPANY ? entity.id : null,
      parentCorporateId: entity.parentCorporateId || null,
      organizationId: entity.organizationId,
      categoryId: category.id,
      categoryName: category.name,
    }))
  );
};

const calculateSummaryFromTasks = (tasks) =>
  tasks.reduce(
    (acc, task) => {
      if (task.lifecycleStatus !== "active") return acc;
      const key = task.summaryStatus;
      if (Object.prototype.hasOwnProperty.call(acc, key)) {
        acc[key] += Number(task.weight || 0);
      }
      return acc;
    },
    { notDue: 0, due: 0, notCompliant: 0, compliant: 0 }
  );

const filters = {
  overdue: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    const isComplete =
      task.status === "completed" && task.summaryStatus === "compliant";
    return !isComplete && isBefore(deadlineDate, now);
  },
  today: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    return (
      (task.status !== "completed" || task.summaryStatus !== "compliant") &&
      isSameDay(deadlineDate, now)
    );
  },
  thisWeek: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    return (
      (task.status !== "completed" || task.summaryStatus !== "compliant") &&
      isWithinInterval(deadlineDate, { start, end })
    );
  },
  nextWeek: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    const start = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
    const end = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
    return (
      (task.status !== "completed" || task.summaryStatus !== "compliant") &&
      isWithinInterval(deadlineDate, { start, end })
    );
  },
  thisMonth: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    const start = startOfMonth(now);
    const end = endOfMonth(now);
    return (
      (task.status !== "completed" || task.summaryStatus !== "compliant") &&
      isWithinInterval(deadlineDate, { start, end })
    );
  },
  nextMonth: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    const start = startOfMonth(addMonths(now, 1));
    const end = endOfMonth(addMonths(now, 1));
    return (
      (task.status !== "completed" || task.summaryStatus !== "compliant") &&
      isWithinInterval(deadlineDate, { start, end })
    );
  },
  thisQuarter: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    const start = startOfQuarter(now);
    const end = endOfQuarter(now);
    return (
      (task.status !== "completed" || task.summaryStatus !== "compliant") &&
      isWithinInterval(deadlineDate, { start, end })
    );
  },
  nextQuarter: (task) => {
    if (!task.deadline || task.lifecycleStatus !== "active") return false;
    const deadlineDate = parseISO(task.deadline);
    const now = new Date();
    const start = startOfQuarter(addQuarters(now, 1));
    const end = endOfQuarter(addQuarters(now, 1));
    return (
      (task.status !== "completed" || task.summaryStatus !== "compliant") &&
      isWithinInterval(deadlineDate, { start, end })
    );
  },
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ComplianceProvider = ({ children }) => {
  // ── Pull organizations from Redux ──────────────────────────────────────────
  const { items: organizations = [] } = useSelector(
    (state) => state.organizationsSlice
  ).organizations;

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  /**
   * Convert the Redux organizations array into compliance corporates.
   * Re-runs only when the organizations array reference changes (i.e. actual
   * Redux state updates), so it won't cause unnecessary re-renders.
   *
   * NOTE: These "slice corporates" are the source-of-truth for identity/metadata.
   * Their categories/tasks will be REPLACED by backend data when available.
   * If the backend returns no data for a corporate we fall back to the default
   * category set built here.
   */
  const sliceCorporates = useMemo(
    () => mapOrgsToCorporates(organizations, currentYear),
    [organizations, currentYear]
  );

  const [data, setData] = useState(() => {
    const defaultData = buildDefaultData();
    return { ...defaultData };
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [usersCache, setUsersCache] = useState(new Map());
  const [loadingUsers, setLoadingUsers] = useState(new Set());
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const isMountedRef = useRef(true);

  const { hasRegistrarRole, hasUploaderRole, isOrgOrSuperAdmin } =
    useComplianceAccess();
  const location = useLocation();

  // ── Sync slice corporates into compliance state ────────────────────────────
  /**
   * Whenever the Redux organizations slice changes we synchronise the
   * compliance `data.corporates` array.  The merge strategy is:
   *
   *  1. For each corporate coming from the slice:
   *     - If the backend has already loaded richer data for it (matching _id),
   *       keep the backend version (categories, weights, etc.) but update
   *       identity/metadata fields from the slice.
   *     - Otherwise use the slice-derived record (with default categories).
   *  2. Retain any backend-only corporates that don't appear in the slice
   *     (edge case: backend knows about entities the slice doesn't yet).
   */
  useEffect(() => {
    if (!sliceCorporates.length || !isMountedRef.current) return;

    if (!isMountedRef.current) return;
    setData((prev) => {
      const backendCorporateMap = new Map(
        prev.corporates.map((c) => [c.id, c])
      );

      const merged = sliceCorporates.map((sliceCorp) => {
        const existing = backendCorporateMap.get(sliceCorp.id);

        if (existing) {
          // Keep backend categories/tasks but refresh identity/metadata from slice
          return {
            ...existing,
            name: sliceCorp.name,
            organizationId: sliceCorp.organizationId,
            registrationNumber: sliceCorp.registrationNumber,
            isRegistrar: sliceCorp.isRegistrar,
            isOrgAdmin: sliceCorp.isOrgAdmin,
            status: sliceCorp.status,
            designation: sliceCorp.designation,
            partnershipType: sliceCorp.partnershipType,
            roles: sliceCorp.roles,
          };
        }

        // No backend data yet — use the slice-derived corporate with
        // recalculated weights on its default categories
        return {
          ...sliceCorp,
          categories: recalculateCategoryWeights(sliceCorp.categories),
        };
      });

      // Also keep any backend-only corporates not present in the slice
      const sliceIds = new Set(sliceCorporates.map((c) => c.id));
      const backendOnly = prev.corporates.filter((c) => !sliceIds.has(c.id));

      return {
        ...prev,
        corporates: [...merged, ...backendOnly],
      };
    });
  }, [sliceCorporates]);

  // ── Fetch compliance data from backend ────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      const publicRoutes = [
        "/",
        "/login",
        "/register",
        "/about",
        "/legal",
        "/contacts",
        "/esign",
        "/verify-account",
      ];
      const currentPath = location.pathname;
      const isPublicRoute = publicRoutes.some(
        (route) =>
          currentPath === route ||
          currentPath.startsWith("/legal") ||
          currentPath.startsWith("/esign")
      );

      const complianceRoutes = [
        "/compliance/org-dashboard",
        "/compliance/super-dashboard",
        "/compliance/tasks",
      ];
      const isComplianceRoute = complianceRoutes.some((route) =>
        currentPath.startsWith(route)
      );

      const token = window.localStorage.getItem("token");
      const isAuthenticated =
        window.localStorage.getItem("isAuthenticated") === "true";
      const hasValidToken = token && token !== "null" && token !== "undefined";

      if (isPublicRoute || !hasValidToken || !isAuthenticated) {
        if (!isInitialized) setIsInitialized(true);
        return;
      }

      if (!isComplianceRoute && hasFetchedData) {
        if (!isInitialized) setIsInitialized(true);
        return;
      }

      if (hasFetchedData && !isComplianceRoute) {
        if (!isInitialized) setIsInitialized(true);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await complianceService.fetchComplianceData();
        const backendData = response.data;
        if (backendData) {
          const backendCorporates = (backendData.corporates || []).map(
            (corporate) => {
              const normalizedCategories = (corporate.categories || []).map(
                (category) => {
                  // Flatten all tasks from all sections if they exist
                  let allTasks = [];
                  if (Array.isArray(category.tasks)) {
                    allTasks = category.tasks;
                  } else if (Array.isArray(category.sections)) {
                    allTasks = category.sections.flatMap((s) => s.tasks || []);
                  }

                  return recalculateTaskWeights({
                    ...category,
                    id: category.id || category._id,
                    tasks: allTasks.map((task) => {
                      const t = { ...task, id: task.id || task._id };
                      const meta = t.metadata || {};
                      return {
                        ...t,
                        timelineType:
                          t.timelineType ||
                          meta.timelineType ||
                          TIMELINE_TYPES.DEFINITE,
                        checklistHeader:
                          t.checklistHeader ?? meta.checklistHeader ?? "",
                        startDate: t.startDate ?? meta.startDate ?? null,
                        deadline: t.deadline ?? meta.deadline ?? null,
                        frequency: t.frequency ?? meta.frequency ?? null,
                      };
                    }),
                  });
                }
              );
              return {
                ...corporate,
                categories: recalculateCategoryWeights(normalizedCategories),
              };
            }
          );

          const companies = (backendData.companies || []).map((company) => {
            const normalizedCategories = (company.categories || []).map(
              (category) => {
                // Flatten all tasks from all sections if they exist
                let allTasks = [];
                if (Array.isArray(category.tasks)) {
                  allTasks = category.tasks;
                } else if (Array.isArray(category.sections)) {
                  allTasks = category.sections.flatMap((s) => s.tasks || []);
                }

                return recalculateTaskWeights({
                  ...category,
                  id: category.id || category._id,
                  tasks: allTasks.map((task) => {
                    const t = { ...task, id: task.id || task._id };
                    const meta = t.metadata || {};
                    return {
                      ...t,
                      timelineType:
                        t.timelineType ||
                        meta.timelineType ||
                        TIMELINE_TYPES.DEFINITE,
                      checklistHeader:
                        t.checklistHeader ?? meta.checklistHeader ?? "",
                      startDate: t.startDate ?? meta.startDate ?? null,
                      deadline: t.deadline ?? meta.deadline ?? null,
                      frequency: t.frequency ?? meta.frequency ?? null,
                      nextCheckDate:
                        t.nextCheckDate ?? meta.nextCheckDate ?? null,
                    };
                  }),
                });
              }
            );
            return {
              ...company,
              categories: recalculateCategoryWeights(normalizedCategories),
            };
          });

          const templates = (backendData.templates || []).map((template) => {
            const normalizedCategories = (template.categories || []).map(
              (category) => {
                // Flatten all tasks from all sections if they exist
                let allTasks = [];
                if (Array.isArray(category.tasks)) {
                  allTasks = category.tasks;
                } else if (Array.isArray(category.sections)) {
                  allTasks = category.sections.flatMap((s) => s.tasks || []);
                }

                return recalculateTaskWeights({
                  ...category,
                  id: category.id || category._id,
                  tasks: allTasks.map((task) => {
                    const t = { ...task, id: task.id || task._id };
                    const meta = t.metadata || {};
                    return {
                      ...t,
                      timelineType:
                        t.timelineType ||
                        meta.timelineType ||
                        TIMELINE_TYPES.DEFINITE,
                      checklistHeader:
                        t.checklistHeader ?? meta.checklistHeader ?? "",
                      startDate: t.startDate ?? meta.startDate ?? null,
                      deadline: t.deadline ?? meta.deadline ?? null,
                      frequency: t.frequency ?? meta.frequency ?? null,
                    };
                  }),
                });
              }
            );
            return {
              ...template,
              id: template.id || template._id,
              categories: normalizedCategories,
              ownerType:
                template.level === "SUPER_ADMIN" ? "superAdmin" : "orgAdmin",
              lastUpdated: template.updatedAt || template.createdAt,
              createdAt: template.createdAt,
              updatedAt: template.updatedAt,
              version: template.version ?? template.versionNumber ?? 1,
            };
          });

          setData((prev) => {
            // ── Merge backend corporates with slice-derived corporates ──────
            // Backend is authoritative for categories/tasks.
            // Slice is authoritative for name/identity/metadata.
            const sliceCorporateMap = new Map(
              sliceCorporates.map((c) => [c.id, c])
            );

            const mergedCorporates = (() => {
              const result = new Map();

              // Start from backend corporates (authoritative for task data)
              backendCorporates.forEach((bc) => {
                const sliceCorp = sliceCorporateMap.get(bc.id);
                result.set(bc.id, {
                  ...bc,
                  // Override identity fields with slice values when available
                  name: sliceCorp?.name || bc.name,
                  organizationId:
                    sliceCorp?.organizationId || bc.organizationId,
                  registrationNumber:
                    sliceCorp?.registrationNumber || bc.registrationNumber,
                  isRegistrar: sliceCorp?.isRegistrar ?? bc.isRegistrar,
                  isOrgAdmin: sliceCorp?.isOrgAdmin ?? bc.isOrgAdmin,
                  status: sliceCorp?.status || bc.status,
                  designation: sliceCorp?.designation || bc.designation,
                  partnershipType:
                    sliceCorp?.partnershipType || bc.partnershipType,
                  roles: sliceCorp?.roles || [],
                });
              });

              // Add slice-only corporates (backend doesn't know about them yet)
              sliceCorporates.forEach((sc) => {
                if (!result.has(sc.id)) {
                  result.set(sc.id, {
                    ...sc,
                    categories: recalculateCategoryWeights(sc.categories),
                  });
                }
              });

              return Array.from(result.values());
            })();

            return {
              ...prev,
              corporates: mergedCorporates,
              companies: companies.length > 0 ? companies : prev.companies,
              orgAdmins:
                backendData.orgAdmins !== undefined
                  ? (backendData.orgAdmins || []).map((admin) => ({
                      ...admin,
                      code:
                        (admin && admin.code) ||
                        admin?.orgAdminCode ||
                        admin?.metadata?.orgAdminCode ||
                        admin?.user?.orgAdminCode ||
                        admin?.user?.code ||
                        "",
                    }))
                  : prev.orgAdmins,
              templates:
                backendData.templates !== undefined
                  ? templates
                  : prev.templates,
              templateAssignments:
                backendData.templateAssignments !== undefined
                  ? backendData.templateAssignments
                  : prev.templateAssignments,
              organizationTemplates:
                backendData.organizationTemplates !== undefined
                  ? backendData.organizationTemplates
                  : prev.organizationTemplates,
              templateHistory:
                backendData.templateHistory !== undefined
                  ? backendData.templateHistory
                  : prev.templateHistory,
              superAdmin: backendData.superAdmin || prev.superAdmin,
              orgAdminProfile:
                backendData.orgAdminProfile !== undefined
                  ? backendData.orgAdminProfile
                  : prev.orgAdminProfile,
              superAdminProfile:
                backendData.superAdminProfile || prev.superAdminProfile,
            };
          });
        }

        if (process.env.NODE_ENV === "development") {
          try {
          } catch (_) {}
        }

        setHasFetchedData(true);
        setIsInitialized(true);
      } catch (err) {
        // console.error(
        //   "[ComplianceProvider] Error fetching compliance data:",
        //   err
        // );
        setError(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load compliance data"
        );
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    hasFetchedData,
    data.superAdmin,
    data.superAdminProfile,
    location.pathname,
    isInitialized,
    sliceCorporates, // Re-fetch when slice corporates change (new org loaded)
  ]);

  // ── Refetch helper ─────────────────────────────────────────────────────────
  const refetchComplianceData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await complianceService.fetchComplianceData();
      const backendData = response.data;

      if (backendData) {
        const backendCorporates = (backendData.corporates || []).map(
          (corporate) => {
            const normalizedCategories = (corporate.categories || []).map(
              (category) => {
                // Flatten all tasks from all sections if they exist
                let allTasks = [];
                if (Array.isArray(category.tasks)) {
                  allTasks = category.tasks;
                } else if (Array.isArray(category.sections)) {
                  allTasks = category.sections.flatMap((s) => s.tasks || []);
                }

                return recalculateTaskWeights({
                  ...category,
                  id: category.id || category._id,
                  tasks: allTasks.map((task) => {
                    const t = { ...task, id: task.id || task._id };
                    const meta = t.metadata || {};
                    return {
                      ...t,
                      timelineType:
                        t.timelineType ||
                        meta.timelineType ||
                        TIMELINE_TYPES.DEFINITE,
                      checklistHeader:
                        t.checklistHeader ?? meta.checklistHeader ?? "",
                      startDate: t.startDate ?? meta.startDate ?? null,
                      deadline: t.deadline ?? meta.deadline ?? null,
                      frequency: t.frequency ?? meta.frequency ?? null,
                    };
                  }),
                });
              }
            );
            return {
              ...corporate,
              categories: recalculateCategoryWeights(normalizedCategories),
            };
          }
        );

        const companies = (backendData.companies || []).map((company) => {
          const normalizedCategories = (company.categories || []).map(
            (category) => {
              // Flatten all tasks from all sections if they exist
              let allTasks = [];
              if (Array.isArray(category.tasks)) {
                allTasks = category.tasks;
              } else if (Array.isArray(category.sections)) {
                allTasks = category.sections.flatMap((s) => s.tasks || []);
              }

              return recalculateTaskWeights({
                ...category,
                id: category.id || category._id,
                tasks: allTasks.map((task) => {
                  const t = { ...task, id: task.id || task._id };
                  const meta = t.metadata || {};
                  return {
                    ...t,
                    timelineType:
                      t.timelineType ||
                      meta.timelineType ||
                      TIMELINE_TYPES.DEFINITE,
                    checklistHeader:
                      t.checklistHeader ?? meta.checklistHeader ?? "",
                    startDate: t.startDate ?? meta.startDate ?? null,
                    deadline: t.deadline ?? meta.deadline ?? null,
                    frequency: t.frequency ?? meta.frequency ?? null,
                    nextCheckDate:
                      t.nextCheckDate ?? meta.nextCheckDate ?? null,
                  };
                }),
              });
            }
          );
          return {
            ...company,
            categories: recalculateCategoryWeights(normalizedCategories),
          };
        });

        const templates = (backendData.templates || []).map((template) => {
          const normalizedCategories = (template.categories || []).map(
            (category) => {
              // Flatten all tasks from all sections if they exist
              let allTasks = [];
              if (Array.isArray(category.tasks)) {
                allTasks = category.tasks;
              } else if (Array.isArray(category.sections)) {
                allTasks = category.sections.flatMap((s) => s.tasks || []);
              }

              return recalculateTaskWeights({
                ...category,
                id: category.id || category._id,
                tasks: allTasks.map((task) => {
                  const t = { ...task, id: task.id || task._id };
                  const meta = t.metadata || {};
                  return {
                    ...t,
                    timelineType:
                      t.timelineType ||
                      meta.timelineType ||
                      TIMELINE_TYPES.DEFINITE,
                    checklistHeader:
                      t.checklistHeader ?? meta.checklistHeader ?? "",
                    startDate: t.startDate ?? meta.startDate ?? null,
                    deadline: t.deadline ?? meta.deadline ?? null,
                    frequency: t.frequency ?? meta.frequency ?? null,
                  };
                }),
              });
            }
          );
          return {
            ...template,
            id: template.id || template._id,
            categories: normalizedCategories,
            ownerType:
              template.level === "SUPER_ADMIN" ? "superAdmin" : "orgAdmin",
            lastUpdated: template.updatedAt || template.createdAt,
            createdAt: template.createdAt,
            updatedAt: template.updatedAt,
            version: template.version ?? template.versionNumber ?? 1,
          };
        });

        if (!isMountedRef.current) {
          return;
        }
        setData((prev) => {
          const sliceCorporateMap = new Map(
            sliceCorporates.map((c) => [c.id, c])
          );

          const mergedCorporates = (() => {
            const result = new Map();

            backendCorporates.forEach((bc) => {
              const sliceCorp = sliceCorporateMap.get(bc.id);
              result.set(bc.id, {
                ...bc,
                name: sliceCorp?.name || bc.name,
                organizationId: sliceCorp?.organizationId || bc.organizationId,
                registrationNumber:
                  sliceCorp?.registrationNumber || bc.registrationNumber,
                isRegistrar: sliceCorp?.isRegistrar ?? bc.isRegistrar,
                isOrgAdmin: sliceCorp?.isOrgAdmin ?? bc.isOrgAdmin,
                status: sliceCorp?.status || bc.status,
                designation: sliceCorp?.designation || bc.designation,
                partnershipType:
                  sliceCorp?.partnershipType || bc.partnershipType,
                roles: sliceCorp?.roles || [],
              });
            });

            sliceCorporates.forEach((sc) => {
              if (!result.has(sc.id)) {
                result.set(sc.id, {
                  ...sc,
                  categories: recalculateCategoryWeights(sc.categories),
                });
              }
            });

            return Array.from(result.values());
          })();

          return {
            ...prev,
            corporates: mergedCorporates,
            companies: companies.length > 0 ? companies : prev.companies,
            orgAdmins:
              backendData.orgAdmins !== undefined
                ? (backendData.orgAdmins || []).map((admin) => ({
                    ...admin,
                    code:
                      (admin && admin.code) ||
                      admin?.orgAdminCode ||
                      admin?.metadata?.orgAdminCode ||
                      admin?.user?.orgAdminCode ||
                      admin?.user?.code ||
                      "",
                  }))
                : prev.orgAdmins,
            templates,
            templateAssignments:
              backendData.templateAssignments !== undefined
                ? backendData.templateAssignments
                : prev.templateAssignments,
            organizationTemplates:
              backendData.organizationTemplates !== undefined
                ? backendData.organizationTemplates
                : prev.organizationTemplates,
            templateHistory:
              backendData.templateHistory !== undefined
                ? backendData.templateHistory
                : prev.templateHistory,
            superAdmin: backendData.superAdmin || prev.superAdmin,
            orgAdminProfile:
              backendData.orgAdminProfile !== undefined
                ? backendData.orgAdminProfile
                : prev.orgAdminProfile,
            superAdminProfile:
              backendData.superAdminProfile || prev.superAdminProfile,
          };
        });
      }

      setHasFetchedData(true);
    } catch (refetchError) {
      console.error("[ComplianceProvider] Error refetching:", refetchError);
      setError(refetchError);
      setHasFetchedData(false);
    } finally {
      setIsLoading(false);
    }
  }, [sliceCorporates]);

  // ── All mutation helpers (unchanged) ──────────────────────────────────────

  const defaultUsersMap = useMemo(() => {
    const map = new Map();
    defaultUsers.forEach((user) => {
      if (user?.id) map.set(user.id, user);
    });
    return map;
  }, []);

  const shouldElevateOrgAdminAccess = useMemo(
    () => hasRegistrarRole || hasUploaderRole || isOrgOrSuperAdmin,
    [hasRegistrarRole, hasUploaderRole, isOrgOrSuperAdmin]
  );

  const updateEntity = (entityId, entityType, updater) => {
    const collectionKey =
      entityType === ENTITY_TYPES.COMPANY ? "companies" : "corporates";
    if (!isMountedRef.current) return;
    if (!isMountedRef.current) return;
    if (!isMountedRef.current) return;
    if (!isMountedRef.current) return;
    setData((prev) => ({
      ...prev,
      [collectionKey]: prev[collectionKey].map((entity) =>
        entity.id === entityId ? updater(entity) : entity
      ),
    }));
  };

  const addCategory = async (
    entityId,
    categoryName,
    entityType = ENTITY_TYPES.CORPORATE
  ) => {
    if (!categoryName) return;
    const newCategory = {
      id: uuidv4(),
      name: categoryName,
      description: "Custom compliance category",
      weight: 0,
      tasks: [createTask()],
    };
    updateEntity(entityId, entityType, (entity) => ({
      ...entity,
      categories: recalculateCategoryWeights([
        ...entity.categories,
        newCategory,
      ]),
    }));
    try {
      if (entityType === ENTITY_TYPES.COMPANY) {
        await complianceService.addCompanyCategory(entityId, newCategory);
      } else {
        await complianceService.addCorporateCategory(entityId, newCategory);
      }
    } catch (err) {
      console.error("[ComplianceProvider] Error adding category:", err);
    }
  };

  const assignTemplateToCompany = async (
    companyId,
    templateId,
    { appliedBy = "Org Admin", origin = "company", code } = {}
  ) => {
    if (!companyId || !templateId) return;
    setData((prev) => {
      const template = prev.templates.find((item) => item.id === templateId);
      const company = prev.companies.find((item) => item.id === companyId);
      if (!template || !company) return prev;
      const now = new Date();
      const timestamp = formatDate(now);
      const historyEntry = {
        id: uuidv4(),
        scope: "company",
        scopeId: companyId,
        templateId: template.id,
        templateName: template.name,
        version: template.version,
        appliedAt: timestamp,
        appliedBy,
        origin,
      };
      const nextCompanies = prev.companies.map((item) => {
        if (item.id !== companyId) return item;
        const categories = recalculateCategoryWeights(
          cloneCategories(template.categories)
        );
        return {
          ...item,
          categories,
          template: {
            templateId: template.id,
            templateName: template.name,
            version: template.version,
            appliedAt: timestamp,
            appliedBy,
            origin,
          },
          templateHistory: [...(item.templateHistory || []), historyEntry],
        };
      });
      return {
        ...prev,
        companies: nextCompanies,
        templateAssignments: {
          ...prev.templateAssignments,
          companies: {
            ...prev.templateAssignments.companies,
            [companyId]: {
              templateId: template.id,
              templateName: template.name,
              version: template.version,
              appliedAt: timestamp,
              appliedBy,
              origin,
            },
          },
        },
        templateHistory: [...prev.templateHistory, historyEntry],
      };
    });
    try {
      await complianceService.assignTemplateToCompany(companyId, {
        templateId,
        appliedBy,
        origin,
        code,
      });
      await refetchComplianceData();
    } catch (err) {
      console.error(
        "[ComplianceProvider] Error assigning template to company:",
        err
      );
    }
  };

  const addTask = async (
    entityId,
    categoryId,
    taskPayload,
    entityType = ENTITY_TYPES.CORPORATE,
    baseCategories = null
  ) => {
    const newTask = createTask(taskPayload);
    updateEntity(entityId, entityType, (entity) => {
      let currentCategories = entity.categories || [];
      if (currentCategories.length === 0 && baseCategories) {
        currentCategories = cloneCategories(baseCategories);
      }
      return {
        ...entity,
        categories: currentCategories.map((category) => {
          if (category.id !== categoryId) return category;
          return recalculateTaskWeights({
            ...category,
            tasks: [...category.tasks, newTask],
          });
        }),
      };
    });
    try {
      if (entityType === ENTITY_TYPES.COMPANY) {
        await complianceService.addCompanyTask(entityId, categoryId, newTask);
      } else {
        await complianceService.addCorporateTask(entityId, categoryId, newTask);
      }
    } catch (err) {
      console.error("[ComplianceProvider] Error adding task:", err);
    }
  };

  const removeTask = (
    entityId,
    categoryId,
    taskId,
    entityType = ENTITY_TYPES.CORPORATE
  ) => {
    updateEntity(entityId, entityType, (entity) => ({
      ...entity,
      categories: entity.categories.map((category) => {
        if (category.id !== categoryId) return category;
        return recalculateTaskWeights({
          ...category,
          tasks: category.tasks.filter((task) => task.id !== taskId),
        });
      }),
    }));
  };

  const updateTask = async (
    entityId,
    categoryId,
    taskId,
    updates,
    entityType = ENTITY_TYPES.CORPORATE,
    baseCategories = null
  ) => {
    // 1. Get current task from state to calculate enriched values for the backend
    const entities =
      entityType === ENTITY_TYPES.COMPANY ? data.companies : data.corporates;
    const entity = entities.find((e) => e.id === entityId);
    let task = null;
    if (entity) {
      const cat = (entity.categories || []).find((c) => c.id === categoryId);
      task = (cat?.tasks || []).find((t) => t.id === taskId);
    }
    if (!task && baseCategories) {
      const cat = (baseCategories || []).find((c) => c.id === categoryId);
      task = (cat?.tasks || []).find((t) => t.id === taskId);
    }

    // 2. Calculate enriched values for both state and backend
    const isSchedulingUpdate =
      entityType === ENTITY_TYPES.COMPANY &&
      (updates.startDate !== undefined ||
        updates.deadline !== undefined ||
        updates.nextCheckDate !== undefined ||
        updates.frequency !== undefined);

    const merged = {
      ...task,
      ...updates,
      timelineType:
        updates.timelineType || task?.timelineType || TIMELINE_TYPES.DEFINITE,
      ...(isSchedulingUpdate ? { lifecycleStatus: "active" } : {}),
    };

    let nextDeadline = merged.deadline;
    let nextCheckDate = merged.nextCheckDate || merged.deadline;
    if (merged.timelineType === TIMELINE_TYPES.INDEFINITE) {
      if (
        merged.status === "completed" &&
        merged.completionDate &&
        merged.frequency
      ) {
        const completion = parseISO(merged.completionDate);
        const nextCheck = addFrequency(completion, merged.frequency);
        nextCheckDate = formatDate(nextCheck);
        nextDeadline = nextCheckDate;
      } else if (merged.nextCheckDate) {
        nextDeadline = merged.nextCheckDate;
        nextCheckDate = merged.nextCheckDate;
      } else if (merged.startDate) {
        nextDeadline = merged.deadline || merged.startDate;
        nextCheckDate = nextDeadline;
      }
    }

    const enriched = { ...merged, deadline: nextDeadline, nextCheckDate };
    const computed = computeTaskSummaryStatus(enriched);
    const finalTask = { ...enriched, ...computed };

    // 3. Prepare payload for backend persistence
    const persistencePayload = {
      ...updates,
      lifecycleStatus:
        merged.lifecycleStatus ??
        updates.lifecycleStatus ??
        task?.lifecycleStatus,
      deadline: nextDeadline,
      nextCheckDate: nextCheckDate,
      timelineType: finalTask.timelineType,
      metadata: {
        ...(task?.metadata || {}),
        timelineType: finalTask.timelineType,
        startDate: finalTask.startDate,
        deadline: finalTask.deadline,
        frequency: finalTask.frequency,
        nextCheckDate: finalTask.nextCheckDate,
        checklistHeader: finalTask.checklistHeader || task?.checklistHeader,
      },
    };

    // 4. Update local state
    updateEntity(entityId, entityType, (ent) => {
      let currentCategories = ent.categories || [];

      // If the entity has no categories but we have baseCategories (from template fallback),
      // initialize the entity's categories by cloning the base.
      if (currentCategories.length === 0 && baseCategories) {
        currentCategories = cloneCategories(baseCategories);
      }

      return {
        ...ent,
        categories: currentCategories.map((category) => {
          if (category.id !== categoryId) return category;
          return recalculateTaskWeights({
            ...category,
            tasks: category.tasks.map((t) => {
              if (t.id !== taskId) return t;
              return finalTask;
            }),
          });
        }),
      };
    });

    // 5. Call backend with the fully enriched payload
    try {
      if (entityType === ENTITY_TYPES.COMPANY) {
        await complianceService.updateCompanyTask(
          entityId,
          categoryId,
          taskId,
          persistencePayload
        );
      } else {
        await complianceService.updateCorporateTask(
          entityId,
          categoryId,
          taskId,
          persistencePayload
        );
      }
    } catch (err) {
      console.error("[ComplianceProvider] Error updating task:", err);
    }
  };

  const updateCategoryWeight = (
    entityId,
    categoryId,
    weight,
    entityType = ENTITY_TYPES.CORPORATE,
    baseCategories = null
  ) => {
    const numericWeight = Number.isNaN(Number(weight)) ? 0 : Number(weight);
    const clampedWeight = Math.min(100, Math.max(0, numericWeight));
    updateEntity(entityId, entityType, (entity) => {
      let currentCategories = entity.categories || [];
      if (currentCategories.length === 0 && baseCategories) {
        currentCategories = cloneCategories(baseCategories);
      }
      if (!currentCategories.length) return entity;
      const otherCategories = currentCategories.filter(
        (c) => c.id !== categoryId
      );
      const totalOtherWeight = otherCategories.reduce(
        (sum, c) => sum + Number(c.weight || 0),
        0
      );
      const remainder = Math.max(0, 100 - clampedWeight);
      let accumulated = 0;
      const updatedCategories = currentCategories.map((category, index) => {
        if (category.id === categoryId) {
          const updated = recalculateTaskWeights({
            ...category,
            weight: roundToTwo(clampedWeight),
          });
          accumulated += updated.weight;
          return updated;
        }
        const ratio =
          totalOtherWeight > 0
            ? Number(category.weight || 0) / totalOtherWeight
            : 1 / Math.max(1, otherCategories.length);
        let newWeight = remainder * ratio;
        if (index === currentCategories.length - 1) {
          newWeight = Math.max(0, 100 - accumulated);
        }
        const updated = recalculateTaskWeights({
          ...category,
          weight: roundToTwo(newWeight),
        });
        accumulated += updated.weight;
        return updated;
      });
      const totalWeight = updatedCategories.reduce(
        (sum, c) => sum + Number(c.weight || 0),
        0
      );
      const diff = roundToTwo(100 - totalWeight);
      if (Math.abs(diff) > 0.01 && updatedCategories[0]) {
        updatedCategories[0].weight = roundToTwo(
          Number(updatedCategories[0].weight || 0) + diff
        );
      }
      return {
        ...entity,
        categories: updatedCategories.map((c) => recalculateTaskWeights(c)),
      };
    });
  };

  const updateTaskLifecycle = (
    entityId,
    categoryId,
    taskId,
    lifecycleStatus,
    entityType = ENTITY_TYPES.CORPORATE,
    baseCategories = null
  ) => {
    if (lifecycleStatus === "delete") {
      removeTask(entityId, categoryId, taskId, entityType);
      return;
    }
    updateTask(
      entityId,
      categoryId,
      taskId,
      { lifecycleStatus },
      entityType,
      baseCategories
    );
  };

  const assignResponsibility = (
    entityId,
    categoryId,
    taskId,
    responsibilities,
    entityType = ENTITY_TYPES.CORPORATE,
    baseCategories = null
  ) => {
    updateTask(
      entityId,
      categoryId,
      taskId,
      { responsibilities },
      entityType,
      baseCategories
    );
  };

  const createTemplate = async (payload) => {
    if (!payload?.name) return null;
    const now = new Date();
    const baseYear = payload.year || now.getFullYear();
    // Build default categories when none provided
    const baseCategories = payload.categories?.length
      ? cloneCategories(payload.categories)
      : buildDefaultCategories(baseYear);

    const backendPayload = {
      name: payload.name,
      description: payload.description || "",
      level: payload.ownerType === "orgAdmin" ? "ORG_ADMIN" : "SUPER_ADMIN",
      type: payload.type || "DEFAULT",
      year: baseYear,
      status: "ACTIVE",
    };
    if (baseCategories.length) {
      const totalCategories = baseCategories.length;
      let accumulatedCategoryWeight = 0;
      const backendCategories = baseCategories.map((category, index) => {
        const tasks = category.tasks || [];
        if (!tasks.length) {
          return {
            name: category.name,
            description: category.description || "",
            weight: 0,
            sections: [],
          };
        }
        const isLastCategory = index === totalCategories - 1;
        const rawCategoryWeight = 100 / totalCategories;
        const categoryWeight = isLastCategory
          ? roundToTwo(100 - accumulatedCategoryWeight)
          : roundToTwo(rawCategoryWeight);
        accumulatedCategoryWeight += categoryWeight;

        const taskCount = tasks.length;
        const rawTaskWeight = categoryWeight / taskCount;
        let accumulatedTaskWeight = 0;
        const backendTasks = tasks.map((task, taskIndex) => {
          const isLastTask = taskIndex === taskCount - 1;
          const taskWeight = isLastTask
            ? roundToTwo(categoryWeight - accumulatedTaskWeight)
            : roundToTwo(rawTaskWeight);
          accumulatedTaskWeight += taskWeight;
          return {
            title: task.name || task.title,
            description: task.description || "",
            weight: taskWeight,
            defaultStatus: "NOT_STARTED",
            dueInDays: task.dueInDays || null,
            isMandatory: task.isMandatory || false,
            mandatoryBy: task.mandatoryBy || null,
          };
        });

        return {
          name: category.name,
          description: category.description || "",
          weight: categoryWeight,
          sections: [
            {
              name: category.name,
              description: category.description || "",
              weight: categoryWeight,
              tasks: backendTasks,
            },
          ],
        };
      });
      backendPayload.categories = backendCategories;
    }
    try {
      const response = await complianceService.createTemplate(backendPayload);
      if (response?.data?.template && isMountedRef.current) {
        const tpl = response.data.template;
        const normalizedCategories = (tpl.categories || []).map((category) =>
          recalculateTaskWeights({
            ...category,
            id: category.id || category._id,
            tasks: (category.tasks || category.sections?.[0]?.tasks || []).map(
              (task) => ({ ...task, id: task.id || task._id })
            ),
          })
        );
        const normalizedTemplate = {
          ...tpl,
          id: tpl.id || tpl._id,
          categories: normalizedCategories,
          ownerType: tpl.level === "SUPER_ADMIN" ? "superAdmin" : "orgAdmin",
          lastUpdated: tpl.updatedAt || tpl.createdAt,
          createdAt: tpl.createdAt,
          version: tpl.version ?? tpl.versionNumber ?? 1,
        };
        setData((prev) => ({
          ...prev,
          templates: [...prev.templates, normalizedTemplate],
        }));
        return normalizedTemplate.id;
      }
      return null;
    } catch (err) {
      console.error("[ComplianceProvider] Error creating template:", err);
      return null;
    }
  };

  const updateTemplateState = (templates, templateId, updater) => {
    const updatedBy = (updater && updater.updatedBy) || "Super Admin";
    const timestamp = formatDate(new Date());
    return templates.map((template) => {
      if (template.id !== templateId) return template;
      const nextTemplate = updater.update(template);
      if (!nextTemplate) return template;
      return {
        ...nextTemplate,
        lastUpdated: timestamp,
        lastUpdatedBy: updatedBy,
      };
    });
  };

  const persistTemplateCategories = async (
    templateId,
    categories,
    { updatedBy = "Super Admin" } = {}
  ) => {
    if (!templateId || !Array.isArray(categories)) return;
    try {
      const template = data.templates.find((t) => t.id === templateId);
      if (!template) return;
      const backendCategories = categories.map((category) => ({
        name: category.name,
        description: category.description || "",
        weight: category.weight || 1,
        sections: [
          {
            name: category.name,
            description: category.description || "",
            weight: 1,
            tasks: (category.tasks || []).map((task) => ({
              title: task.name || task.title,
              description: task.description || "",
              weight: task.weight || 1,
              timelineType: task.timelineType || TIMELINE_TYPES.DEFINITE,
              defaultStatus: "NOT_STARTED",
              dueInDays: task.dueInDays || null,
              isMandatory: task.isMandatory || false,
              mandatoryBy: task.mandatoryBy || null,
              metadata: {
                timelineType: task.timelineType || TIMELINE_TYPES.DEFINITE,
                checklistHeader: task.checklistHeader || "",
                startDate: task.startDate || null,
                deadline: task.deadline || null,
                frequency: task.frequency || null,
              },
            })),
          },
        ],
      }));
      await complianceService.updateTemplate(templateId, {
        categories: backendCategories,
        ...(updatedBy ? { metadata: { updatedBy } } : {}),
      });
    } catch (err) {
      console.error(
        "[ComplianceProvider] Error persisting template categories:",
        err
      );
    }
  };

  const addTemplateCategory = (
    templateId,
    {
      name = "New Compliance Category",
      description = "Custom compliance category",
    } = {},
    { updatedBy = "Super Admin" } = {}
  ) => {
    if (!templateId) return null;
    const template = data.templates.find((t) => t.id === templateId);
    if (!template) return null;
    const categoryId = uuidv4();
    const newCategory = {
      id: categoryId,
      name,
      description,
      weight: 0,
      tasks: [
        createTask({
          title: `${name} Task`,
          description: "Placeholder task for this category.",
        }),
      ],
    };
    const nextCategories = recalculateCategoryWeights([
      ...(template.categories || []),
      newCategory,
    ]);
    setData((prev) => ({
      ...prev,
      templates: updateTemplateState(prev.templates, templateId, {
        updatedBy,
        update: (tpl) =>
          tpl.id === templateId ? { ...tpl, categories: nextCategories } : tpl,
      }),
    }));
    void persistTemplateCategories(templateId, nextCategories, { updatedBy });
    return categoryId;
  };

  const updateTemplateCategory = (
    templateId,
    categoryId,
    updates,
    { updatedBy = "Super Admin" } = {}
  ) => {
    if (!templateId || !categoryId || !updates) return;
    const template = data.templates.find((t) => t.id === templateId);
    if (!template) return;
    const nextCategories = (template.categories || []).map((category) => {
      if (category.id !== categoryId) return category;
      const nextCategory = { ...category, ...updates };
      if (Object.prototype.hasOwnProperty.call(updates, "weight")) {
        return recalculateTaskWeights(nextCategory);
      }
      return nextCategory;
    });
    setData((prev) => ({
      ...prev,
      templates: updateTemplateState(prev.templates, templateId, {
        updatedBy,
        update: (tpl) =>
          tpl.id === templateId ? { ...tpl, categories: nextCategories } : tpl,
      }),
    }));
    void persistTemplateCategories(templateId, nextCategories, { updatedBy });
  };

  const removeTemplateCategory = (
    templateId,
    categoryId,
    { updatedBy = "Super Admin" } = {}
  ) => {
    if (!templateId || !categoryId) return;
    const template = data.templates.find((t) => t.id === templateId);
    if (!template) return;
    const filtered = (template.categories || []).filter(
      (cat) => cat.id !== categoryId
    );
    const nextCategories = recalculateCategoryWeights(filtered);
    setData((prev) => ({
      ...prev,
      templates: updateTemplateState(prev.templates, templateId, {
        updatedBy,
        update: (tpl) =>
          tpl.id === templateId ? { ...tpl, categories: nextCategories } : tpl,
      }),
    }));
    if (nextCategories.length > 0) {
      void persistTemplateCategories(templateId, nextCategories, { updatedBy });
    }
  };

  const addTemplateTask = (
    templateId,
    categoryId,
    taskPayload = {},
    { updatedBy = "Super Admin" } = {}
  ) => {
    if (!templateId || !categoryId) return null;
    const template = data.templates.find((t) => t.id === templateId);
    if (!template) return null;
    const newTask = createTask(taskPayload);
    const nextCategories = (template.categories || []).map((category) => {
      if (category.id !== categoryId) return category;
      return recalculateTaskWeights({
        ...category,
        tasks: [...(category.tasks || []), newTask],
      });
    });
    setData((prev) => ({
      ...prev,
      templates: updateTemplateState(prev.templates, templateId, {
        updatedBy,
        update: (tpl) =>
          tpl.id === templateId ? { ...tpl, categories: nextCategories } : tpl,
      }),
    }));
    void persistTemplateCategories(templateId, nextCategories, { updatedBy });
    return newTask.id;
  };

  const updateTemplateTask = (
    templateId,
    categoryId,
    taskId,
    updates,
    { updatedBy = "Super Admin" } = {}
  ) => {
    if (!templateId || !categoryId || !taskId || !updates) return;
    const template = data.templates.find((t) => t.id === templateId);
    if (!template) return;
    const nextCategories = (template.categories || []).map((category) => {
      if (category.id !== categoryId) return category;
      const tasks = (category.tasks || []).map((task) => {
        if (task.id !== taskId) return task;
        let nextTask = {
          ...task,
          ...updates,
          timelineType:
            updates.timelineType ||
            task.timelineType ||
            TIMELINE_TYPES.DEFINITE,
        };
        if (updates.timelineType) {
          if (updates.timelineType === TIMELINE_TYPES.DEFINITE) {
            nextTask = { ...nextTask, frequency: null };
          } else {
            nextTask = { ...nextTask, deadline: null };
          }
        }
        if (
          nextTask.timelineType === TIMELINE_TYPES.INDEFINITE &&
          !nextTask.frequency
        ) {
          nextTask.frequency = FREQUENCY_OPTIONS[0]?.value || "monthly";
        }
        const computed = computeTaskSummaryStatus(nextTask);
        return { ...nextTask, ...computed };
      });
      return recalculateTaskWeights({ ...category, tasks });
    });
    setData((prev) => ({
      ...prev,
      templates: updateTemplateState(prev.templates, templateId, {
        updatedBy,
        update: (tpl) =>
          tpl.id === templateId ? { ...tpl, categories: nextCategories } : tpl,
      }),
    }));
    void persistTemplateCategories(templateId, nextCategories, { updatedBy });
  };

  const isTaskMandatory = (templateId, categoryId, taskId) => {
    const template = data.templates.find((t) => t.id === templateId);
    if (!template) return false;
    const category = template.categories?.find((c) => c.id === categoryId);
    if (!category) return false;
    const task = category.tasks?.find((t) => t.id === taskId);
    return task?.isMandatory === true;
  };

  const removeTemplateTask = (
    templateId,
    categoryId,
    taskId,
    { updatedBy = "Super Admin", force = false } = {}
  ) => {
    if (!templateId || !categoryId || !taskId)
      return { success: false, reason: "Missing IDs" };
    const template = data.templates.find((t) => t.id === templateId);
    if (!template) return { success: false, reason: "Template not found" };
    if (!force && isTaskMandatory(templateId, categoryId, taskId)) {
      return {
        success: false,
        reason: "Cannot delete mandatory task",
        isMandatory: true,
      };
    }
    const nextCategories = (template.categories || []).map((category) => {
      if (category.id !== categoryId) return category;
      return recalculateTaskWeights({
        ...category,
        tasks: (category.tasks || []).filter((task) => task.id !== taskId),
      });
    });
    setData((prev) => ({
      ...prev,
      templates: updateTemplateState(prev.templates, templateId, {
        updatedBy,
        update: (tpl) =>
          tpl.id === templateId ? { ...tpl, categories: nextCategories } : tpl,
      }),
    }));
    void persistTemplateCategories(templateId, nextCategories, { updatedBy });
    return { success: true };
  };

  const deleteTemplate = async (templateId) => {
    if (!templateId) return;
    setData((prev) => {
      const nextTemplates = prev.templates.filter((t) => t.id !== templateId);
      const nextAssignmentsBase = {
        corporates: { ...(prev.templateAssignments?.corporates || {}) },
        companies: { ...(prev.templateAssignments?.companies || {}) },
      };
      Object.keys(nextAssignmentsBase.corporates).forEach((corporateId) => {
        if (
          nextAssignmentsBase.corporates[corporateId]?.templateId === templateId
        ) {
          nextAssignmentsBase.corporates[corporateId] = null;
        }
      });
      Object.keys(nextAssignmentsBase.companies).forEach((companyId) => {
        if (
          nextAssignmentsBase.companies[companyId]?.templateId === templateId
        ) {
          nextAssignmentsBase.companies[companyId] = null;
        }
      });
      const clearTemplateFromEntity = (entity) =>
        entity?.template?.templateId !== templateId
          ? entity
          : { ...entity, template: null };
      const nextOrgAdmins = prev.orgAdmins.map((admin) =>
        admin.templateId !== templateId
          ? admin
          : {
              ...admin,
              templateId: null,
              templateName: null,
              templateVersion: null,
            }
      );
      return {
        ...prev,
        templates: nextTemplates,
        templateAssignments: {
          ...prev.templateAssignments,
          ...nextAssignmentsBase,
        },
        corporates: prev.corporates.map(clearTemplateFromEntity),
        companies: prev.companies.map(clearTemplateFromEntity),
        orgAdmins: nextOrgAdmins,
        templateHistory: prev.templateHistory.filter(
          (entry) => entry.templateId !== templateId
        ),
      };
    });
    try {
      await complianceService.deleteTemplate(templateId);
    } catch (err) {
      //console.error("[ComplianceProvider] Error deleting template:", err);
    }
  };

  const assignTemplateToCorporate = async (
    corporateId,
    templateId,
    { appliedBy = "Org Admin", origin = "corporate", code } = {}
  ) => {
    if (!corporateId || !templateId) return;
    let resolvedTemplateId = templateId;
    const existingOrgAdmin = data.orgAdmins.find(
      (admin) =>
        (admin.corporateId === corporateId ||
          admin.corporateId?.toString() === corporateId?.toString()) &&
        (admin.templateId === templateId ||
          admin.templateId === resolvedTemplateId ||
          admin.templateId?.toString() === templateId?.toString() ||
          admin.templateId?.toString() === resolvedTemplateId?.toString())
    );
    const existingAssignment =
      data.templateAssignments?.corporates?.[corporateId];
    const assignmentMatchesTemplate =
      existingAssignment?.templateId === templateId ||
      existingAssignment?.templateId === resolvedTemplateId ||
      existingAssignment?.templateId?.toString() === templateId?.toString() ||
      existingAssignment?.templateId?.toString() ===
        resolvedTemplateId?.toString();
    const assignmentExists = !!(existingOrgAdmin || assignmentMatchesTemplate);
    if (assignmentExists) {
      await refetchComplianceData();
      return;
    }
    setData((prev) => {
      const template = prev.templates.find(
        (item) => item.id === resolvedTemplateId
      );
      const corporate = prev.corporates.find((item) => item.id === corporateId);
      if (!template || !corporate) return prev;
      const now = new Date();
      const timestamp = formatDate(now);
      const historyEntry = {
        id: uuidv4(),
        scope: "corporate",
        scopeId: corporateId,
        templateId: template.id,
        templateName: template.name,
        version: template.version,
        appliedAt: timestamp,
        appliedBy,
        origin,
      };
      const nextCorporates = prev.corporates.map((item) => {
        if (item.id !== corporateId) return item;
        const categories = recalculateCategoryWeights(
          cloneCategories(template.categories)
        );
        return {
          ...item,
          categories,
          template: {
            templateId: template.id,
            templateName: template.name,
            version: template.version,
            appliedAt: timestamp,
            appliedBy,
            origin,
          },
          templateHistory: [...(item.templateHistory || []), historyEntry],
        };
      });
      return {
        ...prev,
        corporates: nextCorporates,
        templateAssignments: {
          ...prev.templateAssignments,
          corporates: {
            ...prev.templateAssignments.corporates,
            [corporateId]: {
              templateId: template.id,
              templateName: template.name,
              version: template.version,
              appliedAt: timestamp,
              appliedBy,
              origin,
            },
          },
        },
        templateHistory: [...prev.templateHistory, historyEntry],
      };
    });
    try {
      await complianceService.assignTemplateToCorporate(corporateId, {
        templateId,
        appliedBy,
        origin,
        code,
      });
      await refetchComplianceData();
      if (process.env.NODE_ENV === "development") {
        const debugOrgAdmin = data.orgAdmins?.find(
          (a) =>
            String(a?.corporateId) === String(corporateId) ||
            String(a?.organizationId) === String(corporateId)
        );
        // eslint-disable-next-line no-console
      }
    } catch (err) {
      console.error("[ComplianceProvider] Error assigning template:", err);
      const httpStatus = err?.response?.status;
      const errorStatus = err?.status;
      const message =
        err?.message || err?.messageText || err?.response?.data?.message || "";
      const messageLower = message?.toLowerCase() || "";
      const is409Error =
        httpStatus === 409 ||
        (errorStatus === false && messageLower.includes("already assigned")) ||
        messageLower.includes("already assigned");
      if (is409Error) {
        await refetchComplianceData();
      }
    }
  };

  const saveOrganizationAdmin = (payload) => {
    if (!payload) return null;
    const {
      id,
      name,
      code,
      email,
      phone,
      corporateId,
      organizationId,
      companyName,
      companyRegistrationNumber,
      templateId,
    } = payload;
    const trimmedName = name ? name.trim() : "";
    if (!trimmedName) return null;
    const normalizedCorporateId = corporateId || null;
    const normalizedOrganizationId = (() => {
      if (organizationId === undefined || organizationId === null) return null;
      const trimmed = String(organizationId).trim();
      return trimmed || null;
    })();
    let createdAdminId = id || null;
    if (!isMountedRef.current) return null;
    setData((prev) => {
      const now = formatDate(new Date());
      const template = templateId
        ? prev.templates.find((item) => item.id === templateId)
        : null;
      const templateFields = template
        ? {
            templateId: template.id,
            templateName: template.name,
            templateVersion: template.version,
          }
        : { templateId: null, templateName: null, templateVersion: null };
      const normalizedCode = code ? code.trim() : "";
      const normalizedEmail = email ? email.trim() : "";
      const normalizedPhone = phone ? phone.trim() : "";
      const normalizedCompanyName = companyName ? companyName.trim() : "";
      const normalizedRegistration = companyRegistrationNumber
        ? companyRegistrationNumber.trim()
        : "";
      let adminId = id;
      let previousCorporateId = null;
      const nextOrgAdmins = prev.orgAdmins.map((admin) => {
        if (admin.id !== id) return admin;
        previousCorporateId = admin.corporateId || null;
        adminId = admin.id;
        return {
          ...admin,
          name: trimmedName,
          code: normalizedCode !== "" ? normalizedCode : admin.code,
          email: normalizedEmail,
          phone: normalizedPhone,
          corporateId: normalizedCorporateId,
          organizationId: normalizedOrganizationId,
          companyName: normalizedCompanyName,
          companyRegistrationNumber: normalizedRegistration,
          assignedAt: admin.assignedAt || now,
          updatedAt: now,
          ...templateFields,
        };
      });
      let updatedOrgAdmins = nextOrgAdmins;
      if (!id) {
        adminId = `org-admin-${uuidv4()}`;
        updatedOrgAdmins = [
          ...prev.orgAdmins,
          {
            id: adminId,
            name: trimmedName,
            code: normalizedCode,
            email: normalizedEmail,
            phone: normalizedPhone,
            corporateId: normalizedCorporateId,
            organizationId: normalizedOrganizationId,
            companyName: normalizedCompanyName,
            companyRegistrationNumber: normalizedRegistration,
            assignedAt: now,
            updatedAt: now,
            ...templateFields,
          },
        ];
        createdAdminId = adminId;
      }
      const nextOrgAdminProfile = {
        name: trimmedName,
        email: normalizedEmail || null,
        phone: normalizedPhone || null,
      };
      const nextCorporates = prev.corporates.map((corporate) => {
        if (normalizedCorporateId && corporate.id === normalizedCorporateId) {
          return {
            ...corporate,
            orgAdminProfile: nextOrgAdminProfile,
            organizationId:
              normalizedOrganizationId || corporate.organizationId || null,
          };
        }
        if (
          previousCorporateId &&
          previousCorporateId !== normalizedCorporateId &&
          corporate.id === previousCorporateId
        ) {
          return { ...corporate, orgAdminProfile: null };
        }
        if (!normalizedCorporateId && corporate.id === previousCorporateId) {
          return { ...corporate, orgAdminProfile: null };
        }
        return corporate;
      });
      return {
        ...prev,
        orgAdmins: updatedOrgAdmins,
        corporates: nextCorporates,
      };
    });
    return createdAdminId;
  };

  const deleteOrganizationAdmin = (adminId) => {
    if (!adminId) return;
    setData((prev) => {
      const targetAdmin = prev.orgAdmins.find((admin) => admin.id === adminId);
      if (!targetAdmin) return prev;
      return {
        ...prev,
        orgAdmins: prev.orgAdmins.filter((admin) => admin.id !== adminId),
        corporates: prev.corporates.map((corporate) =>
          corporate.id === targetAdmin.corporateId
            ? { ...corporate, orgAdminProfile: null }
            : corporate
        ),
      };
    });
  };

  const revokeTemplateAssignment = async (assignmentId) => {
    if (!assignmentId) return;
    try {
      await complianceService.revokeTemplateAssignment(assignmentId);
      await refetchComplianceData();
    } catch (err) {
      // console.error(
      //   "[ComplianceProvider] Error revoking template assignment:",
      //   err
      // );
    }
  };

  // ── Derived / memoised values ──────────────────────────────────────────────

  const corporatesMap = useMemo(() => {
    const map = new Map();
    data.corporates.forEach((corporate) => map.set(corporate.id, corporate));
    return map;
  }, [data.corporates]);

  const companiesMap = useMemo(() => {
    const map = new Map();
    data.companies.forEach((company) => map.set(company.id, company));
    return map;
  }, [data.companies]);

  const corporateSummaries = useMemo(
    () =>
      data.corporates.map((corporate) => {
        const tasks = flattenTasks(corporate);
        return {
          ...corporate,
          tasks,
          summary: calculateSummaryFromTasks(tasks),
        };
      }),
    [data.corporates]
  );

  const companySummaries = useMemo(
    () =>
      data.companies.map((company) => {
        const tasks = flattenTasks(company);
        return { ...company, tasks, summary: calculateSummaryFromTasks(tasks) };
      }),
    [data.companies]
  );

  const collectTasksForProfile = useCallback(
    (profile) => {
      if (!profile) return [];
      const corporateTasks = (profile.corporateIds || []).flatMap(
        (corporateId) =>
          corporatesMap.get(corporateId)
            ? flattenTasks(corporatesMap.get(corporateId))
            : []
      );
      const companyTasks = (profile.companyIds || []).flatMap((companyId) =>
        companiesMap.get(companyId)
          ? flattenTasks(companiesMap.get(companyId))
          : []
      );
      return [...corporateTasks, ...companyTasks];
    },
    [companiesMap, corporatesMap]
  );

  const orgAdminSummaryProfile = useMemo(() => {
    if (!data.orgAdminProfile) return null;
    const tasks = collectTasksForProfile(data.orgAdminProfile);
    return {
      ...data.orgAdminProfile,
      tasks,
      summary: calculateSummaryFromTasks(tasks),
    };
  }, [collectTasksForProfile, data.orgAdminProfile]);

  const superSummary = useMemo(() => {
    const profile = data.superAdminProfile;
    const tasks = collectTasksForProfile(profile);
    return {
      ...data.superAdmin,
      code: profile?.code,
      summary: calculateSummaryFromTasks(tasks),
      tasks,
    };
  }, [collectTasksForProfile, data.superAdmin, data.superAdminProfile]);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      try {
      } catch (_) {}
    }
  }, [data.superAdminProfile, superSummary]);

  const getTasksByFilter = (filterKey) => {
    const predicate = filters[filterKey];
    if (!predicate) return [];
    return [
      ...data.corporates.flatMap((corporate) =>
        flattenTasks(corporate).filter(predicate)
      ),
      ...data.companies.flatMap((company) =>
        flattenTasks(company).filter(predicate)
      ),
    ];
  };

  const fetchUsersForEntity = useCallback(
    async (entityId, entityType = ENTITY_TYPES.COMPANY) => {
      if (!entityId) return [];
      const cacheKey = `${entityType}:${entityId}`;
      if (usersCache.has(cacheKey)) return usersCache.get(cacheKey);
      if (loadingUsers.has(cacheKey)) return defaultFallbackUsers;
      if (entityType === ENTITY_TYPES.CORPORATE) {
        setUsersCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, []);
          return next;
        });
        return [];
      }
      setLoadingUsers((prev) => new Set([...prev, cacheKey]));
      try {
        const orgAdminId = data.orgAdminProfile?.corporateId || null;
        const response = await complianceService.getCompanyAssignableUsers(
          entityId,
          orgAdminId
        );
        const backendUsers = response?.data?.data || response?.data || [];
        const transformedUsers = backendUsers.map((user) => ({
          id: user.id || user._id,
          name: user.fullName || user.email || "Unknown User",
          email: user.email,
          title: user.dashboardRoles?.[0] || user.linkedRole || "Employee",
          department: user.isLegalDepartment ? "Legal Department" : "Staff",
          initials: (user.fullName || user.email || "UN")
            .split(" ")
            .map((part) => part.charAt(0).toUpperCase())
            .slice(0, 2)
            .join(""),
          dashboardRoles: user.dashboardRoles || [],
          source: user.source,
        }));
        setUsersCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, transformedUsers);
          return next;
        });
        setLoadingUsers((prev) => {
          const next = new Set(prev);
          next.delete(cacheKey);
          return next;
        });
        return transformedUsers;
      } catch (err) {
        // console.error(
        //   "[ComplianceProvider] Error fetching assignable users:",
        //   err
        // );
        setLoadingUsers((prev) => {
          const next = new Set(prev);
          next.delete(cacheKey);
          return next;
        });
        return defaultFallbackUsers;
      }
    },
    [data.orgAdminProfile?.corporateId, usersCache, loadingUsers]
  );

  const getCachedUsersForEntity = useCallback(
    (entityId, entityType = ENTITY_TYPES.COMPANY) => {
      if (!entityId) return defaultFallbackUsers;
      const cacheKey = `${entityType}:${entityId}`;
      if (usersCache.has(cacheKey)) return usersCache.get(cacheKey);
      return defaultFallbackUsers;
    },
    [usersCache]
  );

  const getUsersForEntity = useCallback(
    (entityId, entityType = ENTITY_TYPES.COMPANY) => {
      if (!entityId) return defaultFallbackUsers;
      const cacheKey = `${entityType}:${entityId}`;
      if (usersCache.has(cacheKey)) return usersCache.get(cacheKey);
      fetchUsersForEntity(entityId, entityType);
      return defaultFallbackUsers;
    },
    [fetchUsersForEntity, usersCache]
  );

  const getUsersForCorporate = useCallback(
    (corporateId) => getUsersForEntity(corporateId, ENTITY_TYPES.CORPORATE),
    [getUsersForEntity]
  );

  const getUsersForCompany = useCallback(
    (companyId) => getUsersForEntity(companyId, ENTITY_TYPES.COMPANY),
    [getUsersForEntity]
  );

  const listCorporateTasks = useCallback(
    (corporateId) => {
      if (!corporateId) return [];
      const corporate = corporatesMap.get(corporateId);
      if (!corporate) return [];
      return flattenTasks(corporate);
    },
    [corporatesMap]
  );

  const listCompanyTasks = useCallback(
    (companyId) => {
      if (!companyId) return [];
      const company = companiesMap.get(companyId);
      if (!company) return [];
      return flattenTasks(company);
    },
    [companiesMap]
  );

  // ── Context value ──────────────────────────────────────────────────────────

  return (
    <ComplianceContext.Provider
      value={{
        corporates: data.corporates,
        companies: data.companies,
        templates: data.templates,
        templateAssignments: data.templateAssignments,
        organizationTemplates: data.organizationTemplates,
        orgAdmins: data.orgAdmins,
        templateHistory: data.templateHistory,
        superSummary,
        superAdminProfile: data.superAdminProfile,
        orgAdminProfile: data.orgAdminProfile,
        orgAdminSummaryProfile,
        corporateSummaries,
        companySummaries,
        addCategory,
        addTask,
        updateTask,
        updateCategoryWeight,
        updateTaskLifecycle,
        assignResponsibility,
        createTemplate,
        addTemplateCategory,
        updateTemplateCategory,
        removeTemplateCategory,
        addTemplateTask,
        updateTemplateTask,
        removeTemplateTask,
        isTaskMandatory,
        deleteTemplate,
        assignTemplateToCorporate,
        assignTemplateToCompany,
        saveOrganizationAdmin,
        deleteOrganizationAdmin,
        revokeTemplateAssignment,
        defaultUsers,
        getUsersForCorporate,
        getUsersForCompany,
        getUsersForEntity,
        getCachedUsersForEntity,
        fetchUsersForEntity,
        listCorporateTasks,
        listCompanyTasks,
        STATUS_OPTIONS,
        LIFECYCLE_OPTIONS,
        FREQUENCY_OPTIONS,
        TIMELINE_TYPES,
        getTasksByFilter,
        isLoading,
        error,
        isInitialized,
        refetchComplianceData,
      }}
    >
      {children}
    </ComplianceContext.Provider>
  );
};

export {
  useComplianceContext,
  ENTITY_TYPES,
  flattenTasks,
  buildDefaultCategories,
};
